/**
 * DoorFunctions Component
 * Manages badge translations for TTS welcome announcements
 * Only accessible to specific super admin emails
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from './axiosInstance';
import { initiateSocket, getSocket } from './socket';
import './styles/DoorFunctions.css';

// Build a compact page window for the pager: always show page 1 and the last
// page, plus a few pages either side of the current one, with '…' gaps between.
function getPageWindow(current, total) {
  if (total <= 1) return [1];
  const delta = 2; // pages shown on each side of the current page
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);
  const pages = [1];
  if (left > 2) pages.push('…');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('…');
  pages.push(total);
  return pages;
}

function DoorFunctions() {
  const [activeTab, setActiveTab] = useState('preferences');
  const [badges, setBadges] = useState([]);
  const [scanHistory, setScanHistory] = useState([]);
  const [scanPagination, setScanPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [jumpPage, setJumpPage] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingBadge, setEditingBadge] = useState(null);
  const [editForm, setEditForm] = useState({
    nickname: '',
    customMessage: '',
    announceEnabled: true,
    useName: false,
    motivationalMode: 'off',
    language: 'en'
  });

  // Sorting state
  const [sortField, setSortField] = useState(null); // 'name', 'scans', 'lastSeen'
  const [sortDirection, setSortDirection] = useState('asc');

  // Scan history filter
  const [employeeFilter, setEmployeeFilter] = useState('');

  const fetchBadges = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/door-functions/badges');
      setBadges(data.badges || []);
    } catch (err) {
      console.error('Error fetching badges:', err);
      setError(err.response?.data?.message || 'Failed to fetch badges');
    }
  }, []);

  const fetchScanHistory = useCallback(async (page = 1, employee = '') => {
    try {
      let url = `/door-functions/scan-history?page=${page}&limit=50`;
      if (employee) {
        url += `&employee=${encodeURIComponent(employee)}`;
      }
      const { data } = await axiosInstance.get(url);
      setScanHistory(data.scans || []);
      setScanPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Error fetching scan history:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/door-functions/stats');
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // Track if we're on the first page (to add new scans)
  const isFirstPage = useRef(true);
  isFirstPage.current = scanPagination.page === 1;

  // Jump to a specific scan-history page, clamped to the valid range. No-op if
  // it's the page already shown (avoids a redundant refetch).
  const goToScanPage = (p) => {
    const target = Math.max(1, Math.min(scanPagination.pages, Number(p) || 1));
    if (target === scanPagination.page) return;
    fetchScanHistory(target, employeeFilter);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([fetchBadges(), fetchScanHistory(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBadges, fetchScanHistory, fetchStats]);

  // WebSocket for real-time badge scan updates
  useEffect(() => {
    initiateSocket();
    const socket = getSocket();

    if (socket) {
      const handleBadgeScan = (scanData) => {
        console.log('[DoorFunctions] Real-time badge scan:', scanData);

        // Add the new scan to the top of the history (only if on page 1)
        if (isFirstPage.current) {
          setScanHistory(prev => {
            // Prepend the new scan, limit to 50
            const newScan = {
              _id: scanData.badgeId + '-' + Date.now(),
              ...scanData
            };
            return [newScan, ...prev].slice(0, 50);
          });

          // Update pagination total
          setScanPagination(prev => ({
            ...prev,
            total: prev.total + 1
          }));
        }

        // Update the badge in the badges list (increment scan count, update lastSeen)
        setBadges(prevBadges => {
          const badgeIndex = prevBadges.findIndex(b => b.badgeId === scanData.badgeId);
          if (badgeIndex >= 0) {
            const updatedBadges = [...prevBadges];
            updatedBadges[badgeIndex] = {
              ...updatedBadges[badgeIndex],
              scanCount: (updatedBadges[badgeIndex].scanCount || 0) + 1,
              lastSeen: scanData.timestamp
            };
            return updatedBadges;
          }
          // New badge - fetch all badges to get the full record
          fetchBadges();
          return prevBadges;
        });

        // Update stats
        setStats(prev => prev ? {
          ...prev,
          totalScans: (prev.totalScans || 0) + 1,
          todayScans: (prev.todayScans || 0) + 1
        } : prev);
      };

      socket.on('badgeScan', handleBadgeScan);

      return () => {
        socket.off('badgeScan', handleBadgeScan);
      };
    }
  }, [fetchBadges]);

  const handleEdit = (badge) => {
    setEditingBadge(badge.badgeId);
    setEditForm({
      nickname: badge.nickname || '',
      customMessage: badge.customMessage || '',
      announceEnabled: badge.announceEnabled !== false,
      useName: badge.useName === true,
      motivationalMode: badge.motivationalMode || 'off',
      language: badge.language || 'en'
    });
  };

  const handleCancelEdit = () => {
    setEditingBadge(null);
    setEditForm({ nickname: '', customMessage: '', announceEnabled: true, useName: false, motivationalMode: 'off', language: 'en' });
  };

  // Quick change language (without entering edit mode)
  const handleLanguageChange = async (badge, newLanguage) => {
    try {
      await axiosInstance.put(`/door-functions/badges/${badge.badgeId}`, {
        language: newLanguage
      });
      fetchBadges();
    } catch (err) {
      alert('Failed to change language: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSave = async (badgeId) => {
    try {
      await axiosInstance.put(`/door-functions/badges/${badgeId}`, editForm);
      setEditingBadge(null);
      fetchBadges();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (badgeId, name) => {
    if (!window.confirm(`Delete badge record for ${name || badgeId}?`)) return;

    try {
      await axiosInstance.delete(`/door-functions/badges/${badgeId}`);
      fetchBadges();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleTestAnnouncement = async (badgeId) => {
    try {
      const { data } = await axiosInstance.post('/door-functions/test-announcement', { badgeId });
      alert(`Announcement sent for: ${data.announced}`);
    } catch (err) {
      alert('Failed to test: ' + (err.response?.data?.message || err.message));
    }
  };

  // Quick toggle for Announce Enabled (without entering edit mode)
  const handleToggleAnnounce = async (badge) => {
    try {
      await axiosInstance.put(`/door-functions/badges/${badge.badgeId}`, {
        announceEnabled: badge.announceEnabled === false ? true : false
      });
      fetchBadges();
    } catch (err) {
      alert('Failed to toggle: ' + (err.response?.data?.message || err.message));
    }
  };

  // Quick toggle for Use Name (without entering edit mode)
  const handleToggleUseName = async (badge) => {
    try {
      await axiosInstance.put(`/door-functions/badges/${badge.badgeId}`, {
        useName: !badge.useName
      });
      fetchBadges();
    } catch (err) {
      alert('Failed to toggle: ' + (err.response?.data?.message || err.message));
    }
  };

  // Quick change for Motivational mode (without entering edit mode)
  const handleMotivationalModeChange = async (badge, newMode) => {
    try {
      await axiosInstance.put(`/door-functions/badges/${badge.badgeId}`, {
        motivationalMode: newMode
      });
      fetchBadges();
    } catch (err) {
      alert('Failed to change mode: ' + (err.response?.data?.message || err.message));
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const getFirstName = (fullName) => {
    if (!fullName) return 'Unknown';
    if (fullName.includes(',')) {
      return fullName.split(',')[1]?.trim().split(' ')[0] || fullName;
    }
    return fullName.split(' ')[0];
  };

  // Handle column header click for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending (or descending for scans)
      setSortField(field);
      setSortDirection(field === 'scans' ? 'desc' : 'asc');
    }
  };

  // Get sorted badges
  const getSortedBadges = () => {
    if (!sortField) return badges;

    return [...badges].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'name':
          aVal = (a.originalName || '').toLowerCase();
          bVal = (b.originalName || '').toLowerCase();
          break;
        case 'scans':
          aVal = a.scanCount || 0;
          bVal = b.scanCount || 0;
          break;
        case 'lastSeen':
          aVal = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
          bVal = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort indicator component
  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span className="sort-indicator">⇅</span>;
    return <span className="sort-indicator active">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Language options
  const LANGUAGES = {
    en: { code: 'en', name: 'English', flag: '🇺🇸' },
    es: { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    ko: { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    ar: { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    de: { code: 'de', name: 'German', flag: '🇩🇪' }
  };

  // Get time-based greeting for preview in the specified language
  const getTimeGreeting = (language = 'en') => {
    const hour = new Date().getHours();

    const greetings = {
      en: { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening', welcome: 'Welcome' },
      es: { morning: 'Buenos días', afternoon: 'Buenas tardes', evening: 'Buenas noches', welcome: 'Bienvenido' },
      ko: { morning: '좋은 아침이에요', afternoon: '안녕하세요', evening: '안녕하세요', welcome: '환영합니다' },
      ar: { morning: 'صباح الخير', afternoon: 'مساء الخير', evening: 'مساء الخير', welcome: 'أهلاً وسهلاً' },
      de: { morning: 'Guten Morgen', afternoon: 'Guten Tag', evening: 'Guten Abend', welcome: 'Willkommen' }
    };

    const lang = greetings[language] || greetings.en;

    if (hour >= 5 && hour < 12) return lang.morning;
    if (hour >= 12 && hour < 17) return lang.afternoon;
    return lang.evening;
  };

  // Sample motivational sayings for preview
  const MOTIVATIONAL_SAMPLES = {
    en: "You're going to crush it today!",
    es: "Hoy vas a arrasar!",
    ko: "오늘 하루도 힘내세요!",
    ar: "ستحقق إنجازات رائعة اليوم!",
    de: "Du wirst es heute rocken!"
  };

  // Get preview of what will be announced
  const getAnnouncementPreview = (badge) => {
    const language = badge.language || 'en';

    // If announcements are disabled entirely
    if (badge.announceEnabled === false) {
      return <span className="text-muted">(No announcement)</span>;
    }

    const greeting = getTimeGreeting(language);
    const name = badge.nickname || getFirstName(badge.originalName);

    // Custom message overrides everything
    if (badge.customMessage) {
      return `"${badge.customMessage.replace('{name}', name).replace('{greeting}', greeting)}"`;
    }

    // Build the announcement based on settings
    let message = greeting;
    if (badge.useName) {
      message += `, ${name}`;
    }
    message += '!';

    // Add motivational sample if enabled (on or once_daily)
    if (badge.motivationalMode && badge.motivationalMode !== 'off') {
      const suffix = badge.motivationalMode === 'once_daily' ? ' (1st scan)' : '';
      message += ` ${MOTIVATIONAL_SAMPLES[language] || MOTIVATIONAL_SAMPLES.en}${suffix}`;
    }

    return `"${message}"`;
  };

  if (loading) {
    return <div className="door-functions-loading">Loading Door Functions...</div>;
  }

  if (error) {
    return (
      <div className="door-functions-error">
        <h3>Access Error</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="door-functions">
      <h2>Door Functions</h2>
      <p className="door-functions-subtitle">
        Manage badge welcome announcements. When someone scans their badge,
        the doorbell speaker will say their name (or custom message).
      </p>

      {/* Stats */}
      {stats && (
        <div className="door-functions-stats">
          <div className="stat-box">
            <span className="stat-value">{stats.totalBadges}</span>
            <span className="stat-label">Badges</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{stats.todayScans}</span>
            <span className="stat-label">Today</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{stats.uniqueBadgesToday}</span>
            <span className="stat-label">Unique Today</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{stats.totalScans}</span>
            <span className="stat-label">All-Time</span>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="door-functions-tabs">
        <button
          className={activeTab === 'preferences' ? 'df-tab active' : 'df-tab'}
          onClick={() => setActiveTab('preferences')}
        >
          Preferences
        </button>
        <button
          className={activeTab === 'history' ? 'df-tab active' : 'df-tab'}
          onClick={() => setActiveTab('history')}
        >
          Scan History ({scanPagination.total.toLocaleString()})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'preferences' && (
        <div className="door-functions-section">
          <div className="section-header">
            <h3>Badge Announcements</h3>
            <button onClick={fetchBadges} className="btn-refresh">Refresh</button>
          </div>

          {badges.length === 0 ? (
            <p className="no-data">No badges registered yet. Scan a badge to add it automatically.</p>
          ) : (
            <div className="badges-table-container">
              <table className="badges-table">
              <thead>
                <tr>
                  <th>Badge ID</th>
                  <th className="sortable" onClick={() => handleSort('name')}>
                    Original Name (TX3) <SortIndicator field="name" />
                  </th>
                  <th>Nickname</th>
                  <th>Language</th>
                  <th>Announcement Preview</th>
                  <th>Announce</th>
                  <th>Use Name</th>
                  <th>Motivate</th>
                  <th className="sortable" onClick={() => handleSort('scans')}>
                    Scans <SortIndicator field="scans" />
                  </th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedBadges().map(badge => (
                  <tr key={badge.badgeId} className={!badge.announceEnabled ? 'disabled-badge' : ''}>
                    <td className="badge-id">{badge.badgeId}</td>
                    <td>{badge.originalName || '(Unknown)'}</td>
                    <td>
                      {editingBadge === badge.badgeId ? (
                        <input
                          type="text"
                          value={editForm.nickname}
                          onChange={e => setEditForm({ ...editForm, nickname: e.target.value })}
                          placeholder={`Default: ${getFirstName(badge.originalName)}`}
                          className="edit-input"
                        />
                      ) : (
                        badge.nickname || <span className="default-name">(uses first name)</span>
                      )}
                    </td>
                    <td className="language-select">
                      {editingBadge === badge.badgeId ? (
                        <select
                          value={editForm.language}
                          onChange={e => setEditForm({ ...editForm, language: e.target.value })}
                          className="edit-select"
                        >
                          {Object.values(LANGUAGES).map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.flag} {lang.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={badge.language || 'en'}
                          onChange={e => handleLanguageChange(badge, e.target.value)}
                          className="inline-select"
                        >
                          {Object.values(LANGUAGES).map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.flag} {lang.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="announcement-preview">
                      {editingBadge === badge.badgeId ? (
                        <input
                          type="text"
                          value={editForm.customMessage}
                          onChange={e => setEditForm({ ...editForm, customMessage: e.target.value })}
                          placeholder="{greeting}, {name}!"
                          className="edit-input"
                        />
                      ) : (
                        <span className={badge.customMessage ? '' : 'default-message'}>
                          {getAnnouncementPreview(badge)}
                        </span>
                      )}
                    </td>
                    <td className="toggle-cell">
                      <label className="toggle-switch" title={badge.announceEnabled !== false ? 'Announcement ON' : 'Announcement OFF'}>
                        <input
                          type="checkbox"
                          checked={badge.announceEnabled !== false}
                          onChange={() => editingBadge === badge.badgeId
                            ? setEditForm({ ...editForm, announceEnabled: !editForm.announceEnabled })
                            : handleToggleAnnounce(badge)
                          }
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td className="toggle-cell">
                      <label className="toggle-switch" title={badge.useName ? 'Include name in greeting' : 'No name in greeting'}>
                        <input
                          type="checkbox"
                          checked={badge.useName === true}
                          onChange={() => editingBadge === badge.badgeId
                            ? setEditForm({ ...editForm, useName: !editForm.useName })
                            : handleToggleUseName(badge)
                          }
                          disabled={badge.announceEnabled === false}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td className="motivational-select">
                      <select
                        value={editingBadge === badge.badgeId ? editForm.motivationalMode : (badge.motivationalMode || 'off')}
                        onChange={(e) => editingBadge === badge.badgeId
                          ? setEditForm({ ...editForm, motivationalMode: e.target.value })
                          : handleMotivationalModeChange(badge, e.target.value)
                        }
                        disabled={badge.announceEnabled === false}
                        title="Off = never, On = every scan, Once Daily = first scan only"
                      >
                        <option value="off">Off</option>
                        <option value="on">On</option>
                        <option value="once_daily">Once Daily</option>
                      </select>
                    </td>
                    <td className="scan-count">{badge.scanCount || 0}</td>
                    <td className="last-seen">{formatTime(badge.lastSeen)}</td>
                    <td className="actions">
                      {editingBadge === badge.badgeId ? (
                        <>
                          <button onClick={() => handleSave(badge.badgeId)} className="btn-save">Save</button>
                          <button onClick={handleCancelEdit} className="btn-cancel">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleEdit(badge)} className="btn-edit">Edit</button>
                          <button onClick={() => handleTestAnnouncement(badge.badgeId)} className="btn-test">Test</button>
                          <button onClick={() => handleDelete(badge.badgeId, badge.originalName)} className="btn-delete">Del</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="door-functions-section">
          <div className="section-header">
            <div className="section-header-left">
              <h3>Scan History</h3>
              <select
                value={employeeFilter}
                onChange={(e) => {
                  setEmployeeFilter(e.target.value);
                  fetchScanHistory(1, e.target.value);
                }}
                className="employee-filter"
              >
                <option value="">All Users</option>
                {[...new Set(badges.filter(b => b.originalName).map(b => b.originalName))]
                  .sort((a, b) => a.localeCompare(b))
                  .map(name => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))
                }
              </select>
            </div>
            <button onClick={() => fetchScanHistory(scanPagination.page, employeeFilter)} className="btn-refresh">Refresh</button>
          </div>

          {scanHistory.length === 0 ? (
            <p className="no-data">No scans recorded yet.</p>
          ) : (
            <>
              <div className="scans-table-container">
                <table className="scans-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Name</th>
                      <th>Badge ID</th>
                      <th>Card #</th>
                      <th>Direction</th>
                      <th>Door</th>
                      <th>Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanHistory.map(scan => (
                      <tr key={scan._id} className={scan.accessGranted ? 'access-granted' : 'access-denied'}>
                        <td>{formatTime(scan.timestamp)}</td>
                        <td>{scan.employeeName || '(Unknown)'}</td>
                        <td className="badge-id">{scan.badgeId}</td>
                        <td>{scan.cardNumber}</td>
                        <td>
                          <span className={`direction ${scan.direction}`}>
                            {scan.direction === 'in' ? 'IN' : scan.direction === 'out' ? 'OUT' : '—'}
                          </span>
                        </td>
                        <td>{scan.door}</td>
                        <td>
                          <span className={scan.accessGranted ? 'granted' : 'denied'}>
                            {scan.accessGranted ? 'GRANTED' : 'DENIED'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination">
                <button
                  disabled={scanPagination.page <= 1}
                  onClick={() => goToScanPage(scanPagination.page - 1)}
                >
                  Previous
                </button>

                <div className="page-numbers">
                  {getPageWindow(scanPagination.page, scanPagination.pages).map((p, i) =>
                    p === '…' ? (
                      <span key={`gap-${i}`} className="page-ellipsis">…</span>
                    ) : (
                      <button
                        key={p}
                        className={p === scanPagination.page ? 'page-num active' : 'page-num'}
                        aria-current={p === scanPagination.page ? 'page' : undefined}
                        onClick={() => goToScanPage(p)}
                      >
                        {p}
                      </button>
                    )
                  )}
                </div>

                <button
                  disabled={scanPagination.page >= scanPagination.pages}
                  onClick={() => goToScanPage(scanPagination.page + 1)}
                >
                  Next
                </button>

                <span className="page-jump">
                  Go to
                  <input
                    type="number"
                    min={1}
                    max={scanPagination.pages}
                    value={jumpPage}
                    onChange={(e) => setJumpPage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && jumpPage) {
                        goToScanPage(jumpPage);
                        setJumpPage('');
                      }
                    }}
                    placeholder={`1–${scanPagination.pages}`}
                  />
                  <button
                    onClick={() => {
                      if (jumpPage) {
                        goToScanPage(jumpPage);
                        setJumpPage('');
                      }
                    }}
                  >
                    Go
                  </button>
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DoorFunctions;
